import React, { useRef, useMemo, useEffect, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import './ParticleText.css';

export default function ParticleText() {
  const [showLogo, setShowLogo] = useState(false);

  return (
    <div className="particle-text-container">
      <Canvas camera={{ position: [0, 0, 120], fov: 50 }} style={{ width: '100%', height: '500px', cursor: 'pointer' }}>
        <ambientLight intensity={0.6} />
        <pointLight position={[50, 50, 50]} intensity={0.8} />
        <Scene showLogo={showLogo} setShowLogo={setShowLogo} />
      </Canvas>
      
    </div>
  );
}

function Scene({ showLogo, setShowLogo }) {
  return (
    <>
      <group onClick={() => setShowLogo(!showLogo)}>
        <GPUParticles showLogo={showLogo} />
      </group>
      {showLogo && <LogoPlane />}
    </>
  );
}

function LogoPlane() {
  const meshRef = useRef();
  const [texture, setTexture] = useState(null);

  useEffect(() => {
    const loader = new THREE.TextureLoader();
    loader.load('/images/logo.png', (loadedTexture) => {
      setTexture(loadedTexture);
    });
  }, []);

  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.1;
    }
  });

  if (!texture) return null;

  return (
    <mesh ref={meshRef} position={[0, 0, 0]}>
      <planeGeometry args={[40, 40]} />
      <meshBasicMaterial map={texture} transparent={true} side={THREE.DoubleSide} />
    </mesh>
  );
}

function GPUParticles({ showLogo }) {
  const pointsRef = useRef();
  const materialRef = useRef();
  const numParticles = 5000;
  const radius = 40;
  const orbitRadius = 60;

  const { positions, normals, rand } = useMemo(() => {
    const positions = new Float32Array(numParticles * 3);
    const normals = new Float32Array(numParticles * 3);
    const rand = new Float32Array(numParticles);

    for (let i = 0; i < numParticles; i++) {
      const u = Math.random();
      const v = Math.random();
      const theta = 2 * Math.PI * u;
      const phi = Math.acos(2 * v - 1);
      const x = radius * Math.sin(phi) * Math.cos(theta);
      const y = radius * Math.sin(phi) * Math.sin(theta);
      const z = radius * Math.cos(phi);
      const si = i * 3;
      positions[si] = x;
      positions[si + 1] = y;
      positions[si + 2] = z;
      const len = Math.sqrt(x * x + y * y + z * z) || 1;
      normals[si] = x / len;
      normals[si + 1] = y / len;
      normals[si + 2] = z / len;
      rand[i] = Math.random();
    }

    return { positions, normals, rand };
  }, [numParticles, radius]);

  useEffect(() => {
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geom.setAttribute('aNormal', new THREE.BufferAttribute(normals, 3));
    geom.setAttribute('aRand', new THREE.BufferAttribute(rand, 1));
    pointsRef.current.geometry = geom;
  }, [positions, normals, rand]);

  const vertexShader = `
    attribute vec3 aNormal;
    attribute float aRand;
    uniform float uPointSize;
    uniform float uProgress;
    varying float vDepth;
    void main() {
      vec3 pos = position;
      
      // When logo shows, move particles outward
      if (uProgress > 0.0) {
        pos = position + aNormal * 20.0 * uProgress;
      }
      
      vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
      gl_PointSize = uPointSize * (300.0 / -mvPosition.z);
      gl_Position = projectionMatrix * mvPosition;
      vDepth = (mvPosition.z + 100.0) / 200.0;
    }
  `;

  const fragmentShader = `
    precision mediump float;
    varying float vDepth;
    void main() {
      vec2 c = gl_PointCoord - vec2(0.5);
      float r = length(c);
      float mask = smoothstep(0.5, 0.48, r);
      vec3 col = vec3(0.4, 0.8, 1.0);
      float brightness = 0.5 + vDepth * 0.5;
      gl_FragColor = vec4(col * brightness, mask);
    }
  `;

  const uniforms = useMemo(() => ({
    uPointSize: { value: 4.0 },
    uProgress: { value: 0 }
  }), []);

  const targetProgress = showLogo ? 1 : 0;

  useFrame((state, delta) => {
    // Animate progress
    if (materialRef.current) {
      const current = materialRef.current.uniforms.uProgress.value;
      materialRef.current.uniforms.uProgress.value += (targetProgress - current) * delta * 3;
    }

    if (pointsRef.current && !showLogo) {
      pointsRef.current.rotation.y += 0.002;
      pointsRef.current.rotation.x += 0.0008;
    } else if (pointsRef.current && showLogo) {
      // Orbit around logo
      pointsRef.current.rotation.y += 0.005;
    }
  });

  return (
    <points ref={pointsRef}>
      <shaderMaterial
        ref={materialRef}
        vertexShader={vertexShader}
        fragmentShader={fragmentShader}
        uniforms={uniforms}
        transparent={true}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}
